// @flow
import React from "react";
import styled from "styled-components";
import { type Moment } from "moment";
import { scrollToById, getImage } from "@nested/utils";
import { getConfig } from "@nested/config";
import { P, H6, H3, H4, media, Link as OriginalLink } from "@nested/ui";
import Swoosh from "../../slices/shared/Swoosh/Swoosh";
import blob from "../../slices/LeftAlignedHero/images/blob.svg";
import { formatDate } from "./utils";

const { APP_URI } = getConfig();

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.blue.light};
`;

const SmallImage = styled.img`
  height: 60px;
  width: 60px;
  position: absolute;
  z-index: 1;
  top: 20px ${media.tablet`
    height: 80px;
    width: 60px;
  `};
`;

const LargeImage = styled.img`
  height: 100px;
  width: 100px;
  position: absolute;
  bottom: 0;
  z-index: 1;
  ${media.tablet`
    height: 120px;
    width: 120px;
  `}
`;

const Bottle = styled(SmallImage)`
  left: -30px;
  ${media.tablet`
    left: -20px;
  `}
`;

const Nappy = styled(SmallImage)`
  right: -30px;
  ${media.tablet`
    right: -20px;
  `}
`;

const Teddy = styled(LargeImage)`
  right: -50px;
  ${media.tablet`
    right: -40px;
  `}
`;

const Duck = styled(LargeImage)`
  left: -50px;
  ${media.tablet`
    left: -40px;
  `}
`;

const TextWrapper = styled.div`
  max-width: 500px;
`;

const Link = styled(OriginalLink)`
  z-index: 2;
  position: relative;
`;

const Background = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 35px 32px;
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.palette.peachy.light};
  position: relative;
  div:last-of-type {
    margin-bottom: 0px;
  }
  ${media.tablet`
    padding: 20px 32px;
  `}
`;

const TextGrouping = styled.div`
  margin-bottom: 30px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  h6,
  h4,
  h3 {
    margin-bottom: 4px;
  }
  h6 {
    line-height: 24px;
  }
`;

const DownArrow = styled.img`
  cursor: pointer;
  display: block;
  height: 100px;
  margin: auto;
  position: relative;
  top: -75px;
  width: 100px;
  ${media.tablet`
  top: -100px;
  `}
`;

type Props = {
  averageCompletionWeeks: number,
  desiredMoveDate: Moment,
  createdCompletionDate: string,
  listDate: string,
  postcode: ?string,
  exampleView: boolean,
  listDateInPast: boolean,
  babyTheme: ?boolean,
};

const EstimatedDates = ({
  averageCompletionWeeks,
  desiredMoveDate,
  createdCompletionDate,
  listDate,
  postcode,
  exampleView,
  listDateInPast,
  babyTheme,
}: Props) => (
  <>
    <Wrapper id="estimated-dates">
      <Swoosh colour="peachy" position="top" shade="light">
        <DownArrow
          src={blob}
          alt="Down arrow"
          onClick={() => scrollToById("estimated-dates")}
        />
      </Swoosh>
      <Background>
        {!exampleView && babyTheme && (
          <>
            <Bottle
              src={getImage("baby-themed-illustrations/Bottle.svg")}
              alt="baby bottle"
            />
            <Nappy
              src={getImage("baby-themed-illustrations/Nappie.svg")}
              alt="nappy"
            />
            <Duck
              src={getImage("baby-themed-illustrations/Duck.svg")}
              alt="rubber duck"
            />
            <Teddy
              src={getImage("baby-themed-illustrations/Bear.svg")}
              alt="teddybear"
            />
          </>
        )}
        {listDateInPast ? (
          <>
            <TextWrapper>
              <H4 center data-test="date-too-soon">
                To move by {formatDate(desiredMoveDate)} it looks like you will
                need to sell faster than is typical for your area.
              </H4>
              <br />
              <H6 center>
                The average time from listing to completion for a property in{" "}
                {postcode || "London"} is
              </H6>
              <H4 center>{averageCompletionWeeks} weeks</H4>
              <br />
              <P center>
                Get in touch with us today and we can help you move on your
                timeline
              </P>
              <Link
                to={`${APP_URI}/get-started`}
                fullWidth
                colour="white"
                inverted
              >
                Get in touch
              </Link>
            </TextWrapper>
          </>
        ) : (
          <>
            <TextGrouping>
              <H6 center>To move home by</H6>
              <H4 className="chromatic-ignore">{createdCompletionDate}</H4>
            </TextGrouping>
            <TextGrouping>
              <H6 center>You should list by</H6>
              <H3
                data-test="estimated-listing-date"
                className="chromatic-ignore"
              >
                {listDate}
              </H3>
            </TextGrouping>
            <TextGrouping>
              <H6 center>
                The average time from listing to completion for a property in{" "}
                {postcode || "London"} is
              </H6>
              <H4>{averageCompletionWeeks} weeks</H4>
            </TextGrouping>
          </>
        )}
      </Background>
    </Wrapper>
    <Swoosh colour="peachy" position="bottom" shade="light" />
  </>
);

export default EstimatedDates;
